import React, { useEffect, useState, useRef, useCallback } from "react";
import ToastProvider, { useToast } from "../ToastProvider";
import utils from "../../utils"; 
import useDbApiKey from "../../useDbApiKey";
import VPOPHeader from "./VPOPHeader";  
import VPOPvaccineProgram from "./VPOPvaccineProgram"; 
import VPOPOutputs from "./VPOPOutputs";
import TableDynamic from "../Layout/Table/TableDynamic";
// import ToastDynamic from "../Layout/ToastDynamic/ToastDynamic";


function VPOPbudgetSimulatorApp({ tenantSetting }) {
  const { addToast } = useToast();

  const TOTAL_VACCINE_GROUPS = 12;

  const vaccinePrograms = [
    {vaccineProgram:'DTP-Containing Vaccine (Penta & Hexa)', vaccineGroup: 'DTP'},
    {vaccineProgram:'HPV', vaccineGroup: 'HPV'},
    {vaccineProgram:'IPV', vaccineGroup: 'IPV'},
    {vaccineProgram:'Measles-Containing Vaccine (M & MR)', vaccineGroup: 'MCV'},
    {vaccineProgram:'PCV', vaccineGroup: 'Pneumococcal'},
    {vaccineProgram:'Rota', vaccineGroup: 'Rotavirus'},
    {vaccineProgram:'YF', vaccineGroup: 'YF'},
    {vaccineProgram:'JE', vaccineGroup: 'JE'},
    {vaccineProgram:'Malaria', vaccineGroup: 'Malaria'},
    {vaccineProgram:'Meningitis (MenA & Multivalent Mening.)', vaccineGroup: 'Meningococcal'},
    {vaccineProgram:'OCV', vaccineGroup: 'Cholera'},
    {vaccineProgram:'TCV', vaccineGroup: 'Typhoid'},
  ];

  const { apiKey } = useDbApiKey();
  const [ countries, setCountries ] = useState([]);
  const [ selectedISO, setSelectedISO ] = useState("");
  const [ aggregatedData, setAggregatedData ] = useState({});
  const [ productOptions, setProductOptions ] = useState([]);
  const [ productDictionary, setProductDictionary ] = useState({});
  const [ campaignTypeOptions, setCampaignTypeOptions ] = useState([]);
  const [ isFirstLoad, setIsFirstLoad ] = useState(true);
  const [ apiCallsInProgress, setApiCallsInProgress ] = useState(new Set());
  const [ apiCallsCompleted, setApiCallsCompleted ] = useState(new Set());

  // Use a ref to track baseline count to avoid stale closure issues
  const baselineCountRef = useRef(0);
  // Use a ref to track which vaccine groups have already reported baseline
  // to prevent double-counting from the productDictionary useEffect re-firing
  const baselineReportedRef = useRef(new Set());

  // Load countries once on mount
  useEffect(() => {
    if (!apiKey) return;
    utils.getData("get_analysis_countries", apiKey, "")
      .then((result) => {
        if (Array.isArray(result)) setCountries(result);
      })
      .catch((err) => {
        if (addToast) addToast({ title: "Error", body: "Error loading countries." });
        console.error("Error loading countries:", err);
      });
  }, [apiKey, addToast]);

  // Load campaign types and product characteristics in parallel once on mount
  useEffect(() => {
    if (!apiKey) return;
    (async () => {
      try {
        const [campaignTypes, routineProducts] = await Promise.all([
          utils.getData('get_vpop_campaign_types', apiKey),
          utils.getData("get_vpop_product_characteristics", apiKey),
        ]);

        if (Array.isArray(campaignTypes)) {
          setCampaignTypeOptions(campaignTypes);
        }

        if (Array.isArray(routineProducts)) {
          const updatedProductDictionary = {};
          const formatted = routineProducts.map(item => {
            updatedProductDictionary[item.product_characteristic_name] = item;
            updatedProductDictionary[item.product_characteristic_id] = item;
            return item.product_characteristic_name;
          });
          setProductDictionary(updatedProductDictionary);
          setProductOptions(formatted);
        }
      } catch (error) {
        if (addToast) addToast({ title: "Error", body: "Error loading reference data." });
        console.error("Error loading reference data:", error);
      }
    })();
  }, [apiKey, addToast]);

  // Reset everything when country changes
  useEffect(() => {
    if (selectedISO) {
      setIsFirstLoad(true);
      setAggregatedData({});
      baselineCountRef.current = 0;
      baselineReportedRef.current = new Set();
      setApiCallsInProgress(new Set());
      setApiCallsCompleted(new Set());
    }
  }, [selectedISO]);
  

  const findSelectedCountryInfo = useCallback((iso) => {
    return countries.find(c => c.ISO === iso);
  }, [countries]);



const handleDataInputsFromChild = useCallback((data, vaccineGroup, type) => {

  if (type === "no_program") {
    baselineCountRef.current += 1;
    if (baselineCountRef.current >= TOTAL_VACCINE_GROUPS) {
      setIsFirstLoad(false);
    }
  }

  if (type === "baseline" && isFirstLoad) {
    if (baselineReportedRef.current.has(vaccineGroup)) return;
    baselineReportedRef.current.add(vaccineGroup);

    setAggregatedData(prevState => {
      const updatedState = { ...prevState };
      if (!updatedState.baseline) updatedState.baseline = [];
      updatedState.baseline = [...updatedState.baseline, data];
      if (!updatedState.optimized) updatedState.optimized = [];
      updatedState.optimized = [
        ...updatedState.optimized,
        data.map(item => ({ ...item, scenario: 'optimized' }))
      ];
      return updatedState;
    });

    baselineCountRef.current += 1;
    if (baselineCountRef.current >= TOTAL_VACCINE_GROUPS) {
      setIsFirstLoad(false);
    }
  }

  if (type === "optimized") {
    setAggregatedData(prevState => {
      const updatedState = { ...prevState };
      const existing = (updatedState.optimized || []).map(arr => [...arr]);

      if (existing.length === 0) {
        updatedState.optimized = [data];
      } else {
        let foundGroup = false;
        const newOptimized = existing.map((existingArray) => {
          if (existingArray[0]?.row_id?.startsWith(vaccineGroup)) {
            foundGroup = true;
            return data;
          }
          return existingArray;
        });
        if (!foundGroup) {
          newOptimized.push(data);
        }
        updatedState.optimized = newOptimized;
      }
      return updatedState;
    });
  }

}, [isFirstLoad]);


// Handle API call status tracking
const handleApiCallStart = useCallback((vaccineGroup, callType) => {
  // Use a unique token per request so concurrent calls for the same
  // vaccineGroup/callType do not collide in the Set.
  const token = Symbol(`${vaccineGroup}-${callType}-inflight`);
  setApiCallsInProgress(prev => new Set([...prev, token]));
}, []);

const handleApiCallComplete = useCallback((vaccineGroup, callType) => {
  // Decrement the in-progress count by removing any one token.
  setApiCallsInProgress(prev => {
    if (!prev || prev.size === 0) {
      return prev;
    }
    const newSet = new Set(prev);
    const iterator = newSet.values();
    const first = iterator.next();
    if (!first.done) {
      newSet.delete(first.value);
    }
    return newSet;
  });
  // Track completion with a unique token so size reflects total completed calls.
  const completedToken = Symbol(`${vaccineGroup}-${callType}-complete`);
  setApiCallsCompleted(prev => new Set([...(prev || []), completedToken]));
}, []);

// Check if all API calls are complete
const areAllApiCallsComplete = useCallback(() => {
  return !isFirstLoad && apiCallsInProgress.size === 0 && apiCallsCompleted.size > 0;
}, [isFirstLoad, apiCallsInProgress, apiCallsCompleted]);





  const getTableData = useCallback(() => {
    const allData = [
      ...(aggregatedData?.baseline || []).flat(),
      ...(aggregatedData?.optimized || []).flat(),
    ];
    return allData.sort((a, b) => 
      (a.vaccine?.localeCompare(b.vaccine) || 0) || 
      (a.type?.localeCompare(b.type) || 0) || 
      (a.scenario?.localeCompare(b.scenario) || 0) ||
      ((a.year ?? 0) - (b.year ?? 0))
    );
  }, [aggregatedData]);

  return (
    <ToastProvider>
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-8">VPOP Budget Simulator</h1>
        <VPOPHeader
          countries={countries}
          selectedISO={selectedISO}
          onSelectISO={(country) => setSelectedISO(country)}
        />
        {selectedISO ? (
          <>
            <div className="mb-06 p-06 border rounded-[var(--border-corner-radius-rounding-05)]">
              {vaccinePrograms.map((program, index) => (
                <VPOPvaccineProgram
                  key={`${selectedISO}-${program.vaccineGroup}`}
                  lastItem={index === vaccinePrograms.length - 1}
                  vaccineProgram={program}
                  selectedISO={findSelectedCountryInfo(selectedISO)}
                  sendDataInputsToParent={handleDataInputsFromChild}
                  productOptions={productOptions}
                  campaignTypeOptions={campaignTypeOptions}
                  productDictionary={productDictionary}
                  onApiCallStart={handleApiCallStart}
                  onApiCallComplete={handleApiCallComplete}
                />
              ))}
              <VPOPOutputs
                countryInfo={findSelectedCountryInfo(selectedISO)}
                aggregatedData={aggregatedData}
                areAllApiCallsComplete={areAllApiCallsComplete()}
              />
              <div className="overflow-y-auto max-height-600">
                <TableDynamic
                  handleSortColumn={() => {}} 
                  data={aggregatedData ? getTableData() : []}
                  configType={'VPOP_TABLE_CONFIG'} 
                  selectableRows={false}
                  className="w-full"
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </ToastProvider>
  );
}

export default VPOPbudgetSimulatorApp;